<template>
  <ShortcutWrapper propInitiatingKey="o" :propShortcuts="bidShortcuts" @shortcut="onTractShortcut">
  <v-row justify-center data-testid="tract-table">
    <v-col md="12">
      <material-card
      :title="isGrouped ? $t('tractGroups') : $t('tracts')"
      :elevation="0"
      full-width>
        <v-container fluid class="ma-n2 pa-0">
          <DataTable
          :items.sync="tableItems"
          :headers="headers"
          :loading.sync="isLoading"
          :customCells="customCells"
          @refresh="handleRefresh"
          @create="emitTableEvent(null, 'new-tract')"
          :actions="tableActions"
          htmlIdentifier="tract-table"
          resize
          :tableHeightCalculationData="tableHeightCalculationData"
          @click:row="onRowClick"
          showPointerCursor>
             <template #custom-controls>
               <v-col cols="auto" class="ml-2" v-if="!showTableFilterSlot">
                 <TractFilterChips v-if="!isGrouped" :filter="filter" @remove-filter="removeFilter"/>
               </v-col>
               <v-col cols="auto" v-if="!isGrouped">
                 <Icon
                   elementId="identify-tract"
                   v-if="deviceIsMobile"
                   icon="mdi-map-marker-radius"
                  :small="false"
                  :tooltipText="$t('identifyTract')"
                  @icon-clicked="$emit('identify-tract')"
                />
              </v-col>
              <v-col cols="auto" v-if="!isGrouped">
                <TractContextMenu @download-csv="downloadCSV" @download-pt-form="openPtFormDialog"/>
              </v-col>
              <v-col cols="auto" v-if="!isGrouped">
                <Icon
                @icon-clicked="myTractsClicked"
                :small="false"
                :icon="IconHelpers.getIconForObjectType('myTracts', filter.myTracts)"
                margin="mr-2"
                :tooltipText="filter.myTracts ? $t('allTracts') : $t('myTracts')"
                elementId="my-tracts-tract-table"/>
              </v-col>
              <v-col cols="auto" v-if="!isGrouped">
                <TractFilter
                appendElementId="tract-table"
                :filter="filter"
                 @filter-applied="applyFilter"/>
              </v-col>
              <v-col cols="auto" v-if="!isGrouped">
                <Icon
                margin="mr-2"
                :small="false"
                icon="mdi-map"
                iconColor="primary"
                elementId="tract-table-map-icon"
                :tooltipText="$t('viewMap')"
                @icon-clicked="interactiveMap = true"/>
              </v-col>
              <v-col cols="auto" v-if="!isGrouped">
                <Icon
                margin="mr-2"
                :small="false"
                icon="mdi-gavel"
                iconColor="primary"
                elementId="bid-manager-icon"
                :tooltipText="$t('bids')"
                @icon-clicked="openBidManager()"/>
              </v-col>
              <v-col cols="auto" v-if="tractGroupsAvailable">
                <Icon
                @icon-clicked="toggleGrouped"
                :small="false"
                :icon="isGrouped ? 'mdi-crosshairs-gps' : 'mdi-arrange-send-to-back'"
                margin="mr-2"
                :tooltipText="isGrouped ? $t('viewTracts') : $t('viewTractGroups')"
                />
              </v-col>
               <v-col cols="auto" class="pr-1">
                 <TabbedColumnAdjuster
                   :propColumnSet="columnSet"
                   :propTabs="columnAdjusterTabs"
                   :dynamicSize="deviceIsMobile"
                   @new-columns-selected="newColumnsSelected"
                   appendElementId="tract-table"
                 />
               </v-col>
               <v-col cols="auto">
                 <Icon
                   margin="mr-2"
                   :small="false"
                   icon="mdi-refresh"
                   :tooltipText="$t('refresh')"
                   @icon-clicked="handleRefresh"
                   elementId="refresh-tracts"
                 />
               </v-col>
               <v-col cols="auto" v-if="userAssignedClaim(UserClaims.ContractManager)">
                 <SplitButton
                   color="primary"
                   icon="mdi-plus"
                   :label="$t('tract')"
                   elementId="create-tract"
                   @primary="emitTableEvent(null, 'new-tract')"
                   :hideChevron="!tractGroupsAvailable"
                 >
                   <template #menu>
                     <v-list-item @click="emitTableEvent(null, 'new-tract-group')">
                       <v-list-item-icon class="mr-2">
                         <v-icon small>mdi-arrange-send-to-back</v-icon>
                       </v-list-item-icon>
                       <v-list-item-content>
                         <v-list-item-title>{{ $t('tractGroup') }}</v-list-item-title>
                       </v-list-item-content>
                     </v-list-item>
                   </template>
                 </SplitButton>
               </v-col>
             </template>
            <template #filters v-if="showTableFilterSlot">
              <TractFilterChips v-if="!isGrouped" :filter="filter" @remove-filter="removeFilter"/>
            </template>
            <template #status="{item}">
              <TractStatusIcon :status="item.status"/>
              <Icon
              v-if="dateIsUnderCountUnitsAway(item.harvestEndDate, 1, 'week')"
              icon="mdi-alert-outline"
              iconColor="warning"
              :tooltipText="$t('tractHarvestWarning1w')"
              />
              <Icon
              v-if="item.paused"
              icon="mdi-pause-circle"
              iconColor="warning"
              :tooltipText="$t('tractPaused')"
              />
            </template>
            <template #name="{item}">
              <span>
                <Icon
                v-if="item.tractCertifications?.length > 0"
                icon="mdi-certificate-outline"
                iconColor="black"
                :tooltipText="$t('tractIsCertified')"
                />
                {{item.name}}
              </span>
            </template>
            <template #land-owner="{item}">
              <span v-if="item.landOwnerAccountName || item.landOwnerName">
                {{item.landOwnerAccountName || item.landOwnerName}}
              </span>
              <span v-else-if="item.landownerCount > 1" @click="emitTableEvent(item, 'tract-detail')" style="cursor: pointer;">
                <v-tooltip bottom color="black">
                  <template #activator="{on}">
                    <v-icon v-on="on" color="black">
                      mdi-dots-horizontal
                    </v-icon>
                  </template>
                  <span class="subtitle-1 white--text">
                    {{ $t('multipleLandowners') }}
                  </span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ $t('notAvailable') }}
              </span>
            </template>
            <template #default-logger="{item}">
              <AccountName
                :accountId="item.loggerAccountId"
                :certificationStatus="item.loggerAccountCertificationStatus"
              >
                <span>
                  {{item.defaultLoggerName || $t('notAvailable')}}
                </span>
              </AccountName>
            </template>
            <template #forester-user="{item}">
              <span>
                {{item.foresterUserName ?? $t('notAvailable')}}
              </span>
            </template>
            <template #cost="{item}">
              <span>
                {{formatMoney(item.cost)}}
              </span>
            </template>
            <template #advance-balance="{item}">
              <span>
                  {{formatMoney(item.advanceBalance)}}
              </span>
            </template>
            <template #harvested-tons="{item}">
              <span>
                {{item.harvestedTons !== 0 ? numberWithCommas(item.harvestedTons, 3) : 0}}
              </span>
            </template>
            <template #expected-tons="{item}">
              <span>
                {{item.expectedTons !== 0 ? numberWithCommas(item.expectedTons, 3) : 0}}
              </span>
            </template>
            <template #harvested-mbf="{item}">
              <span>
                {{calculateHarvestedMBF(item.tractContents)}}
              </span>
            </template>
            <template #expected-mbf="{item}">
              <span>
                {{calculateExpectedMBF(item.tractContents)}}
              </span>
            </template>
            <template #purchase-date="{item}">
              <span>
                {{utcToLocalDate(item.purchaseDate)}}
              </span>
            </template>
            <template #bonds="{item}">
              <span>
                {{formatMoney(item.bondBalance)}}
              </span>
            </template>
            <template #availability-start="{item}">
              <span>
                {{utcToLocalDate(item.availabilityStartDate)}}
              </span>
            </template>
            <template #availability-end="{item}">
              <span>
                {{utcToLocalDate(item.availabilityEndDate)}}
              </span>
            </template>
            <template #harvest-start="{item}">
              <span>
                {{utcToLocalDate(item.harvestStartDate)}}
              </span>
            </template>
            <template #harvest-end="{item}">
              <span>
                {{utcToLocalDate(item.harvestEndDate)}}
              </span>
            </template>
            <template #days-till-harvest-end="{item}">
                <v-tooltip bottom color='#D15F27'>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >{{computeDaysRemainingTillHarvestEndForUI(item)}}</span>
                  </template>
                  <span class="subtitle-1 white--text" style="white-space: pre-line">Days</span>
                </v-tooltip>
            </template>

            <template #consulting-forester="{item}">
              <span>{{ item.consultingForesterAccount ?? $t('notAvailable') }}</span>
            </template>
            <template #rainfall="{item}">
              <v-tooltip
              color="primary"
              open-on-hover
              bottom>
                <template v-slot:activator="{on}">
                  <span v-on="on">
                    <span v-if="item.rainfallDataUpdatedOn">
                      {{ `${item.singleDayRainfallInches}"` }}
                    </span>
                    <span v-else>
                      {{ $t('notAvailable') }}
                    </span>
                  </span>
                </template>
                <span class="subtitle-1 white--text" style="white-space: pre-line">
                  <span v-if="item.rainfallDataUpdatedOn">
                    {{ $t('twentyFourHourPrecipitationUpdatedOn', { updatedOn: tractRainfallUpdated(item) }) }}
                  </span>
                  <span v-else>
                    {{ $t('precipitationDataUnavailable') }}
                  </span>
                </span>
            </v-tooltip>
          </template>
          <template #balance="{item}">
            <v-tooltip
            color="primary"
            open-on-hover
            bottom
            v-if="!isGrouped">
              <template v-slot:activator="{on}">
                <span v-on="on">
                  <span v-if="item.balanceUpdatedOn">
                    {{ balanceTextForTract(item) }}
                  </span>
                  <span v-else>
                    {{ $t('notAvailable') }}
                  </span>
                </span>
              </template>
              <span class="subtitle-1 white--text" style="white-space: pre-line">
                <span v-if="item.balanceUpdatedOn">
                  {{ $t('balanceLastUpdated', { updatedOn: dateStringToLocal(item.balanceUpdatedOn) }) }}
                </span>
                <span v-else>
                  {{ $t('tractDoesNotUseDepletion') }}
                </span>
              </span>
            </v-tooltip>
            <span v-else>{{ formatMoney(item.balance) }}</span>
          </template>
          <template #cost-balance="{item}">
            <v-tooltip
            color="primary"
            open-on-hover
            bottom>
              <template v-slot:activator="{on}">
                <span v-on="on">
                  <span v-if="item.balanceUpdatedOn">
                    {{ costBalanceTextForTract(item) }}
                  </span>
                  <span v-else>
                    {{ $t('notAvailable') }}
                  </span>
                </span>
              </template>
              <span class="subtitle-1 white--text" style="white-space: pre-line">
                <span v-if="item.balanceUpdatedOn">
                  {{ $t('balanceLastUpdated', { updatedOn: dateStringToLocal(item.balanceUpdatedOn) }) }}
                </span>
                <span v-else>
                  {{ $t('tractDoesNotUseDepletion') }}
                </span>
              </span>
            </v-tooltip>
          </template>
          <template #team="{item}">
            <v-row no-gutters>
              <v-col cols="auto">
                <span>{{ item.teamName ?? $t('notAvailable') }}</span>
              </v-col>
              <v-col cols="auto">
                <Icon
                v-if="!!item.teamName && item.users"
                iconColor="black"
                icon="mdi-dots-horizontal"
                margin="ml-1"
                :tooltipText="getTeamTooltip(item.users)"
                />
              </v-col>
            </v-row>
          </template>
          <template #scaling-region="{item}">
            <span v-if="displayScalingRegion(item)"> {{ ScalingRegion.fromInt(item.scalingRegion) }} </span>
          </template>
          <template #related="slotProps">
            <Icon
            margin="mr-2"
            :small="false"
            icon="mdi-information-outline"
            elementId="detail"
            :tooltipText="$t('tractDetail')"
            @icon-clicked="emitTableEvent(slotProps.item, 'tract-detail')"/>
          </template>
          <template #actions="slotProps">
            <td @click.stop.prevent class="text-center cursor-default">
              <template v-if="isGrouped">
                <Icon
                icon="mdi-pencil"
                iconColor="success"
                elementId="edit-tract-group"
                :tooltipText="$t('edit')"
                @icon-clicked="emitTableEvent(slotProps.item, 'edit-tract-group')"
                :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
                <Icon
                icon="mdi-delete-forever"
                iconColor="error"
                elementId="delete-tract-group"
                :tooltipText="$t('delete')"
                @icon-clicked="emitTableEvent(slotProps.item, 'delete-tract-group')"
                :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
              </template>
              <template v-else>
                <Icon
                :icon="slotProps.item.paused ? 'mdi-pause-circle' : 'mdi-pause-circle-outline'"
                iconColor="black"
                elementId="pause"
                :tooltipText="slotProps.item.paused ? $t('unpauseTract') : $t('pauseTract')"
                @icon-clicked="emitTableEvent(slotProps.item, 'pause-tract')"
                :disabled="!userAssignedClaim(UserClaims.ContractManager)"
                />
                <Icon
                icon="mdi-pencil"
                iconColor="success"
                elementId="edit"
                :tooltipText="$t('edit')"
                @icon-clicked="emitTableEvent(slotProps.item, 'edit-tract')"
                :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
                <Icon
                icon="mdi-delete-forever"
                iconColor="error"
                elementId="delete"
                :tooltipText="$t('delete')"
                @icon-clicked="emitTableEvent(slotProps.item, 'delete-tract')"
                :disabled="!userAssignedClaim(UserClaims.ContractManager)"/>
              </template>
            </td>
          </template>
        </DataTable>
      </v-container>
      </material-card>
      <v-dialog v-model="ptFormDialog" width="50vw">
        <TractPtFormDownload @close-form="ptFormDialog = false" v-if="ptFormDialog"/>
      </v-dialog>
      <v-dialog
      v-model="interactiveMap"
      max-width="80vw"
      :fullscreen="$vuetify.breakpoint.smAndDown">
        <InteractiveMap
        :height="$vuetify.breakpoint.smAndDown ? '100%' : '80vh'"
        :tracts.sync="mappedTracts"
        v-if="interactiveMap"
        includeSearchByTract
        includeTractDetailLink
        :showUserLocation="deviceIsMobile"
        :propFilter="filter"
        @filter-applied="applyFilter"
        @tract-detail="onMapTractDetail"
        @map-closed="interactiveMap = false"/>
      </v-dialog>
      <BidManager ref="bidManager" @tract-detail="onBidManagerTractDetail"/>
    </v-col>
  </v-row>
  </ShortcutWrapper>
</template>

<script>
import { TractFilter } from '@/model/Tract.js'
import { mapGetters, mapActions } from 'vuex'
import { TractTypeCategory, TractStatus, UserClaims, ScalingRegion, TractTypeSpecialization } from '@/utils/Enumerations.js'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { numberWithCommas, formatMoney } from '@/utils/NumericMutations'
import { utcToLocalDate, timeBetween, dateIsUnderCountUnitsAway } from '@/utils/DateFormatter.js'
import { generateCsvString } from '@/utils/CSVUtility'
import TractHeaders from '@/headers/Tract'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { isMobile } from '@/utils/DeviceUtility.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import IconHelpers from '@/utils/IconHelpers'
import { kebabCase } from 'lodash'
import { ACRES_DECIMALS } from '@/utils/map/GeospatialHelpers'

export default {
  name: 'TractTable',

  components: {
    InteractiveMap: () => import('@/components/maps/InteractiveMap.vue'),
    TractFilter: () => import('@/components/tract/TractFilter.vue'),
    TractContextMenu: () => import('@/components/tract/TractContextMenu.vue'),
    TractPtFormDownload: () => import('@/components/tract/tract-detail/TractPtFormDownload.vue'),
    SplitButton: () => import('@/components/core/SplitButton.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TabbedColumnAdjuster: () => import('@/components/helper/TabbedColumnAdjuster.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    TractStatusIcon: () => import('@/components/tract/TractStatusIcon.vue'),
    AccountName: () => import('@/components/account/AccountName.vue'),
    TractFilterChips: () => import('@/components/tract/TractFilterChips.vue'),
    BidManager: () => import('@/components/tract/BidManager.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue')
  },

  props: {
    dialogOpen: Boolean,
    propFilter: Object
  },

  data: () => ({
    ScalingRegion,
    TractStatus,
    IconHelpers,
    mapView: false,
    interactiveMap: false,
    ptFormDialog: false,
    columnSet: null,
    filter: {
      status: [], // Active status has value 3.
      selectedTractTypes: [],
      selectedHarvestTypes: [],
      selectedLoggingMethods: [],
      selectedForesters: [],
      selectedTeams: [],
      myTracts: false
    },
    UserClaims,
    isGrouped: false,
    bidShortcuts: [{ key: 'b', id: 'open-bids' }]
  }),

  computed: {
    ...mapGetters('tract', ['allTracts', 'tractsLoading', 'allTractGroups', 'tractGroupsLoading']),
    ...mapGetters('user', ['userInfo']),
    ...mapGetters('tract-type', ['allTractTypes']),

    headers () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    isLoading () {
      return this.isGrouped ? this.tractGroupsLoading : this.tractsLoading
    },

    tableItems () {
      return this.isGrouped ? this.mappedTractGroups : this.mappedTracts
    },

    tableActions () {
      return []
    },

    columnAdjusterTabs () {
      return this.isGrouped ? ['properties'] : ['properties', 'location', 'analysis']
    },

    mappedTracts () {
      const passesMyTracts = (t) => !this.filter.myTracts ||
          t.foresterUser?.applicationUserId === this.userInfo.applicationUserId ||
          t.users?.some(u => u.applicationUserId === this.userInfo.applicationUserId)
      const passesHarvestTypes = (t) => this.filter.selectedHarvestTypes.length === 0 || this.filter.selectedHarvestTypes.map(ht => ht.harvestTypeId).includes(t.harvestType?.harvestTypeId)
      const passesLoggingMethods = (t) => this.filter.selectedLoggingMethods.length === 0 || this.filter.selectedLoggingMethods.map(lm => lm.loggingMethodId).includes(t.loggingMethod?.loggingMethodId)

      return this.allTracts
        .filter(passesMyTracts)
        .filter(passesHarvestTypes)
        .filter(passesLoggingMethods)
        .map(t => ({
          ...t,
          acres: numberWithCommas(t.acres, ACRES_DECIMALS),
          harvestType: t.harvestType?.name,
          loggingMethod: t.loggingMethod?.name,
          timberCategory: t.timberCategory?.name,
          conversion: t.conversion?.name,
          landownerType: t.landownerType?.name,
          landownerBundle: JSON.stringify({
            loAccount: t.landOwnerAccountName,
            loCount: t.landownerCount
          }),
          costBalance: Math.max(0, t.balance)
        }))
    },

    mappedTractGroups () {
      const passesMyTracts = (tg) => !this.filter.myTracts ||
          tg.foresterUserId === this.userInfo.applicationUserId

      return this.allTractGroups
        .filter(passesMyTracts)
        .map(tg => ({
          ...tg,
          category: this.categoryFromInt(tg.tractTypeCategory),
          tractTypeName: tg.tractTypeName || '',
          foresterUserName: tg.foresterUserName || '',
          landownerBundle: JSON.stringify({
            loAccount: tg.landOwnerName,
            loCount: tg.landownerCount || 0
          }),
          consultingForesterName: tg.consultingForesterAccountName || '',
          teamName: tg.teamName || '',
          businessEntityName: tg.businessEntityName || '',
          tractCount: tg.tractCount || 0,
          landownerCount: tg.landownerCount || 0
        }))
    },

    customCells () {
      return [
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'name',
          value: 'name'
        },
        {
          slotName: 'default-logger',
          value: 'defaultLoggerName'
        },
        {
          slotName: 'forester-user',
          value: 'foresterUserName'
        },
        {
          slotName: 'land-owner',
          value: 'landownerBundle'
        },
        {
          slotName: 'cost',
          value: 'cost'
        },
        {
          slotName: 'advance-balance',
          value: 'advanceBalance'
        },
        {
          slotName: 'harvested-tons',
          value: 'harvestedTons'
        },
        {
          slotName: 'expected-tons',
          value: 'expectedTons'
        },
        {
          slotName: 'harvested-mbf',
          value: 'harvestedMbf'
        },
        {
          slotName: 'expected-mbf',
          value: 'expectedMbf'
        },
        {
          slotName: 'purchase-date',
          value: 'purchaseDate'
        },
        {
          slotName: 'bonds',
          value: 'bondBalance'
        },
        {
          slotName: 'availability-start',
          value: 'availabilityStartDate'
        },
        {
          slotName: 'availability-end',
          value: 'availabilityEndDate'
        },
        {
          slotName: 'harvest-start',
          value: 'harvestStartDate'
        },
        {
          slotName: 'harvest-end',
          value: 'harvestEndDate'
        },
        {
          slotName: 'days-till-harvest-end',
          value: 'millisecondsTillHarvestEnd'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'related',
          value: 'related'
        },
        {
          slotName: 'consulting-forester',
          value: 'consultingForesterAccount'
        },
        {
          slotName: 'rainfall',
          value: 'singleDayRainfallInches'
        },
        {
          slotName: 'balance',
          value: 'balance'
        },
        {
          slotName: 'team',
          value: 'teamName'
        },
        {
          slotName: 'cost-balance',
          value: 'costBalance'
        },
        {
          slotName: 'scaling-region',
          value: 'scalingRegion'
        }
      ]
    },

    filtersView () {
      let result = []
      if (this.filter.myTracts) result = result.concat({ filterType: 'myTracts', name: this.$t('myTracts') })
      const statuses = this.filter.status.map(s => ({ filterType: 'status', name: TractStatus.fromInt(s), status: s }))
      const tractTypes = this.filter.selectedTractTypes.map(tt => ({ filterType: 'tractType', name: tt.name }))
      const harvestTypes = this.filter.selectedHarvestTypes.map(ht => ({ filterType: 'harvestType', name: ht.name }))
      const loggingMethods = this.filter.selectedLoggingMethods.map(lm => ({ filterType: 'loggingMethod', name: lm.name }))
      const foresters = this.filter.selectedForesters.map(f => ({ filterType: 'forester', name: f.name }))
      const teams = this.filter.selectedTeams.map(t => ({ filterType: 'team', name: t.name }))
      result = result.concat(statuses)
        .concat(tractTypes)
        .concat(harvestTypes)
        .concat(loggingMethods)
        .concat(foresters)
        .concat(teams)
      return result
    },

    deviceIsMobile () {
      return isMobile()
    },

    showTableFilterSlot () {
      const maxFiltersToInlineWithSearch = this.$vuetify.breakpoint.width / 300
      return this.$vuetify.breakpoint.lgAndDown ||
        this.filtersView.length > maxFiltersToInlineWithSearch
    },

    tableHeightCalculationData () {
      return {
        offset: this.$vuetify.breakpoint.xl && !this.showTableFilterSlot ? 329 : 353
      }
    },

    tractGroupsAvailable () {
      return this.allTractTypes?.some(tt => tt.specialization === TractTypeSpecialization.ObligationUncapped.value || tt.specialization === TractTypeSpecialization.ObligationCapped.value)
    }
  },

  watch: {
    isGrouped: {
      handler (newValue) {
        this.initializeHeaders()
        if (newValue && this.allTractGroups.length === 0 && !this.tractGroupsLoading) {
          this.fetchTractGroups()
        }
      },
      immediate: false
    }
  },

  async created () {
    this.initializeHeaders()
    this.setFilter()
    await this.fetchTractTypes()
    this.initializeHeaders()
  },

  methods: {
    ...mapActions('tract', ['fetchTractGroups']),
    ...mapActions('tract-type', ['fetchTractTypes']),
    categoryFromInt: (x) => TractTypeCategory.fromInt(x),
    userAssignedClaim,
    numberWithCommas,
    formatMoney,
    utcToLocalDate,
    timeBetween,
    dateIsUnderCountUnitsAway,

    openBidManager () {
      this.$refs.bidManager.open()
    },

    onBidManagerTractDetail (row) {
      const tract = this.allTracts.find(t => t.tractId === row.tractId) ?? { tractId: row.tractId }
      this.emitTableEvent(tract, 'tract-detail')
    },

    onMapTractDetail (tract) {
      this.interactiveMap = false
      this.emitTableEvent(tract, 'tract-detail')
    },

    onTractShortcut (id) {
      if (id === 'open-bids') this.openBidManager()
    },

    initializeHeaders () {
      const columnSet = this.isGrouped ? TractHeaders.tractGroupHeaders() : TractHeaders.tractHeaders()
      if (!this.isGrouped && !this.tractGroupsAvailable) {
        columnSet.columns = columnSet.columns.filter(column => column.value !== 'tractGroupName')
      }
      columnSet.columns.forEach(column => column.setCellClass(`tract-table-${kebabCase(column.value)} tract-table-cell`))
      this.columnSet = columnSet
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    downloadCSV () {
      const csvString = generateCsvString(this.allTracts, [
        { label: 'status', value: tract => TractStatus.fromInt(tract.status) || undefined },
        { label: 'name', value: tract => tract.name },
        { label: 'code', value: tract => tract.code },
        { label: 'category', value: tract => tract.type ? this.categoryFromInt(tract.type.category) : undefined },
        { label: 'tractType', value: tract => tract.type?.name },
        ...(this.tractGroupsAvailable ? [{ label: 'tractGroup', value: tract => tract.tractGroupName }] : []),
        { label: 'harvestType', value: tract => tract.harvestType?.name },
        { label: 'loggingMethod', value: tract => tract.loggingMethod?.name },
        { label: 'supplier', value: tract => tract.supplierAccount },
        { label: 'landOwner', value: tract => tract.landownerCount > 1 ? 'M' : tract.landOwnerAccountName || '' },
        { label: 'timberCategory', value: tract => tract.timberCategory?.name },
        { label: 'conversion', value: tract => tract.conversion?.name },
        { label: 'landownerType', value: tract => tract.landownerType?.name },
        { label: 'logger', value: tract => tract.defaultLoggerName },
        { label: 'team', value: tract => tract.teamName },
        { label: 'forester', value: tract => tract.foresterUserName },
        { label: 'consultingForester', value: tract => tract.consultingForesterAccount },
        { label: 'cost', value: tract => `$${tract.cost}` },
        { label: 'costBalance', value: tract => tract.balanceUpdatedOn ? `$${Math.max(tract.balance, 0)}` : this.$t('notAvailable') },
        { label: 'depletionBalance', value: tract => tract.balanceUpdatedOn ? `$${tract.balance}` : this.$t('notAvailable') },
        { label: 'advanceBalance', value: tract => `$${tract.advanceBalance}` },
        { label: 'harvestedTons', value: tract => tract.harvestedTons },
        { label: 'expectedTons', value: tract => tract.expectedTons },
        { label: 'harvestedMBF', value: tract => this.calculateHarvestedMBF(tract.tractContents) },
        { label: 'expectedMBF', value: tract => this.calculateExpectedMBF(tract.tractContents) },
        { label: 'cutoutPercentage', value: tract => tract.harvestPercentage },
        { label: 'rpi', value: tract => tract.rpi },
        { label: 'dbh', value: tract => tract.dbh },
        { label: 'age', value: tract => tract.age },
        { label: 'loggingAccessibility', value: tract => tract.loggingAccessibilityTag },
        { label: 'precipitationInInches', value: tract => tract.rainfallDataUpdatedOn ? tract.singleDayRainfallInches : this.$t('notAvailable') },
        { label: 'acres', value: tract => tract.acres },
        { label: 'harvestedTonsPerAcre', value: tract => tract.harvestedTonsPerAcre },
        { label: 'county', value: tract => tract.spot.countrySecondarySubdivision },
        { label: 'state', value: tract => tract.spot.countrySubdivision },
        { label: 'purchaseDate', value: tract => tract.purchaseDate ? utcToLocalDate(tract.purchaseDate) : undefined },
        { label: 'bonds', value: tract => `$${tract.bondBalance}` },
        { label: 'availabilityStart', value: tract => tract.availabilityStartDate ? utcToLocalDate(tract.availabilityStartDate) : undefined },
        { label: 'availabilityEnd', value: tract => tract.availabilityEndDate ? utcToLocalDate(tract.availabilityEndDate) : undefined },
        { label: 'harvestStart', value: tract => tract.harvestStartDate ? utcToLocalDate(tract.harvestStartDate) : undefined },
        { label: 'harvestEnd', value: tract => tract.harvestEndDate ? utcToLocalDate(tract.harvestEndDate) : undefined },
        { label: 'daysRemainingUntilHarvestEnd', value: tract => tract.harvestEndDate ? this.computeDaysRemainingTillHarvestEndForCSV(tract) : undefined }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `Tracts-${moment().format()}.csv`)
    },

    openPtFormDialog () {
      this.ptFormDialog = true
    },

    getCertificationString (tract) {
      const certifications = tract.certifications

      switch (certifications.length) {
        case 0:
          return this.$t('none')
        case 1:
          return certifications[0].code === '' ? this.$t('notAvailable') : certifications[0].code
        default:
          return this.$t('multiple')
      }
    },

    emitTableEvent (tract, type) {
      const event = {
        actionType: type,
        tract: tract
      }

      this.$emit('tract-action', event)
    },

    emitFilter () {
      const newFilter = new TractFilter()
      newFilter.includeInitiated = this.filter.status.includes(0)
      newFilter.includeCruised = this.filter.status.includes(1)
      newFilter.includeExecuted = this.filter.status.includes(2)
      newFilter.includeActive = this.filter.status.includes(3)
      newFilter.includeClosed = this.filter.status.includes(4)
      newFilter.includeArchived = this.filter.status.includes(5)
      newFilter.selectedForesters = this.filter.selectedForesters
      newFilter.selectedTractTypes = this.filter.selectedTractTypes
      newFilter.selectedTeams = this.filter.selectedTeams

      if (this.isGrouped) {
        this.fetchTractGroups()
      }

      this.$emit('filter-applied', newFilter)
    },

    applyFilter (filter) {
      this.filter = filter
      this.emitFilter()
    },

    computeDaysRemainingTillHarvestEndForUI (tract) {
      if (tract.millisecondsTillHarvestEnd <= 0) {
        return '0'
      }
      if (tract.millisecondsTillHarvestEnd <= moment.duration(1, 'day').asMilliseconds()) {
        return '1'
      }
      if (tract.millisecondsTillHarvestEnd >= moment.duration(30, 'day').asMilliseconds()) {
        return '30+'
      }
      return timeBetween(Date.now(), tract.harvestEndDate).split(' ')[0]
    },

    computeDaysRemainingTillHarvestEndForCSV (tract) {
      if (tract.millisecondsTillHarvestEnd <= 0) {
        return '0'
      }
      if (tract.millisecondsTillHarvestEnd <= moment.duration(1, 'day').asMilliseconds()) {
        return '1'
      }
      return timeBetween(Date.now(), tract.harvestEndDate).split(' ')[0]
    },

    removeFilter (toRemove) {
      switch (toRemove.filterType) {
        case 'status':
          this.filter.status = this.filter.status.filter(s => TractStatus.fromInt(s) !== toRemove.name)
          localStorage.setItem(LocalStorageKeys.TRACT_STATUS_FILTER, this.filter.status)
          break
        case 'tractType':
          this.filter.selectedTractTypes = this.filter.selectedTractTypes.filter(tt => tt.name !== toRemove.name)
          break
        case 'harvestType':
          this.filter.selectedHarvestTypes = this.filter.selectedHarvestTypes.filter(ht => ht.name !== toRemove.name)
          break
        case 'loggingMethod':
          this.filter.selectedLoggingMethods = this.filter.selectedLoggingMethods.filter(lm => lm.name !== toRemove.name)
          break
        case 'forester':
          this.filter.selectedForesters = this.filter.selectedForesters.filter(f => f.name !== toRemove.name)
          break
        case 'team':
          this.filter.selectedTeams = this.filter.selectedTeams.filter(t => t.name !== toRemove.name)
          break
        case 'myTracts':
          this.filter.myTracts = false
          localStorage.setItem(LocalStorageKeys.TRACT_FILTER_MY_TRACTS, this.filter.myTracts)
          break
      }
      this.emitFilter()
    },

    dateStringToLocal (field) {
      return moment.utc(field).local().format('L LT')
    },

    tractRainfallUpdated (tract) {
      return tract
        ? moment.utc(tract.rainfallDataUpdatedOn).local().format('L LT')
        : undefined
    },

    setFilter () {
      if (this.propFilter.includeInitiated) { this.filter.status.push(0) }
      if (this.propFilter.includeCruised) { this.filter.status.push(1) }
      if (this.propFilter.includeExecuted) { this.filter.status.push(2) }
      if (this.propFilter.includeActive) { this.filter.status.push(3) }
      if (this.propFilter.includeClosed) { this.filter.status.push(4) }
      if (this.propFilter.includeArchived) { this.filter.status.push(5) }
      this.filter.selectedForesters = this.propFilter.selectedForesters
      this.filter.selectedTractTypes = this.propFilter.selectedTractTypes
      this.filter.selectedTeams = this.propFilter.selectedTeams
      this.filter.myTracts = localStorage.getItem(LocalStorageKeys.TRACT_FILTER_MY_TRACTS) === 'true'
      this.isGrouped = localStorage.getItem(LocalStorageKeys.TRACT_GROUPS_IS_GROUPED) === 'true'
    },

    balanceTextForTract (tract) {
      return tract.type.specialization === TractTypeSpecialization.Depletion.value ? formatMoney(tract.balance) : this.$t('notAvailable')
    },

    costBalanceTextForTract (tract) {
      return tract.type.specialization === TractTypeSpecialization.Depletion.value ? formatMoney(tract.costBalance) : this.$t('notAvailable')
    },

    getTeamTooltip (tractUsers) {
      return tractUsers
        .map((u) => u.name)
        .toString()
        .replaceAll(',', '\n')
    },

    myTractsClicked () {
      this.filter.myTracts = !this.filter.myTracts
      localStorage.setItem(LocalStorageKeys.TRACT_FILTER_MY_TRACTS, this.filter.myTracts)
    },

    toggleGrouped () {
      this.isGrouped = !this.isGrouped
      localStorage.setItem(LocalStorageKeys.TRACT_GROUPS_IS_GROUPED, this.isGrouped)
    },

    handleRefresh () {
      if (this.isGrouped) {
        this.fetchTractGroups()
      } else {
        this.$emit('refresh-tracts')
      }
    },

    colorForTractStatus ({ status }) {
      const color = TractStatus.forInt(status).details.color
      if (color !== 'secondary') return color
      return 'white'
    },

    onRowClick ({ data }) {
      if (this.isGrouped) {
        this.emitTableEvent(data, 'tract-group-detail')
      } else {
        this.emitTableEvent(data, 'tract-detail')
      }
    },

    displayScalingRegion (item) {
      return item.scalingRegion !== ScalingRegion.NotDefined.value
    },

    calculateHarvestedMBF (tractContents) {
      const volumeBased = tractContents?.some(tc => tc.speciesId !== null)
      if (!volumeBased) return ''

      return this.numberWithCommas(tractContents.filter(tc => tc.speciesId !== null).reduce((sum, tc) => sum + (tc.harvested || 0), 0) / 1000, 3)
    },

    calculateExpectedMBF (tractContents) {
      const volumeBased = tractContents?.some(tc => tc.speciesId !== null)
      if (!volumeBased) return ''

      return this.numberWithCommas(tractContents.filter(tc => tc.speciesId !== null).reduce((sum, tc) => sum + (tc.expected || 0), 0) / 1000, 3)
    }
  }
}
</script>
